/*Get treatment requests*/
import {
    NotificationPayload,
    PaginationParams,
    PaginationResponse,
    PatientAgeRange,
    ResponseType,
    TableFiltersPayload,
    TableOrder
} from '@/types.ts'
import axios, {parseResponseData} from '@/axiosInstance.ts'
import {
    TreatmentRequestAuthorization,
    TreatmentRequestsColumn,
    TreatmentRequestScore,
    TreatmentRequestSmall,
    TreatmentRequestTherapistDoctor
} from '@/features/treatment-requests/types.ts'
import {z} from 'zod'
import {captureException} from '@sentry/react'

export interface HttpGetTreatmentRequestsOptions {
    params: PaginationParams & {
        search?: string
        status?: TreatmentRequestSmall['status']
    }
    payload?: TableFiltersPayload & {
        sorts?: TableOrder[]
    }
}
export const httpGetTreatmentRequests = ({params, payload}: HttpGetTreatmentRequestsOptions) =>
    axios.post('treatment-requests/index', payload, {params})

/*Export treatment requests*/
interface HttpExportTreatmentRequestsOptions {
    payload: {
        selectedTreatmentRequestIds: string[]
        exceptedTreatmentRequestIds: string[]
        type: 'xlsx' | 'xls' | 'csv'
        filters?: TableFiltersPayload['filters']
        fields?: TreatmentRequestsColumn[]
        sorts?: TableOrder[]
    }
}
export const httpExportTreatmentRequests = ({payload}: HttpExportTreatmentRequestsOptions) =>
    axios.post('treatment-requests/export', payload, {responseType: 'blob'})

/*Change treatment requests status*/
export interface HttpChangeTreatmentRequestsOptions {
    payload: {
        selectedTreatmentRequestIds: string[]
        exceptedTreatmentRequestIds: string[]
    } & (
        | {
              status: Exclude<TreatmentRequestSmall['status'], 'in_progress' | 'rejected'>
          }
        | {
              status: 'in_progress'
              notificationDetails?: string
              notificationRecipientIds: string[]
          }
        | {
              status: 'rejected'
              rejectedReason: NonNullable<TreatmentRequestSmall['rejectedReason']>
              notificationDetails?: string
              notificationRecipientIds: string[]
          }
    )
}
export const httpChangeTreatmentRequestsStatus = ({payload}: HttpChangeTreatmentRequestsOptions) =>
    axios.patch(`treatment-requests/change-status`, payload)

/*Get diagnosis*/
export interface HttpGetTreatmentRequestsDiseasesOptions {
    params: {
        serviceType?: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
        search?: string
        parentId?: string
        responseType: ResponseType
    } & PaginationParams
}

export const httpGetDiseases = ({params}: HttpGetTreatmentRequestsDiseasesOptions) => axios.get(`/diseases`, {params})

/*Get disease*/
interface HttpGetDiseaseOptions {
    urlParams: {diseaseId: string}
    params: {responseType: ResponseType}
}

export const httpGetDisease = ({urlParams, params}: HttpGetDiseaseOptions) =>
    axios.get(`diseases/${urlParams.diseaseId}`, {params})

/*Create treatment request*/
export const HttpCreateTreatmentRequestPayload = z
    .object({
        authorization: TreatmentRequestAuthorization.nullable(),
        authorizationDate: z.string().nullish(),
        authorizationToRequest: z.boolean(),
        diseaseId: z.string().min(1),
        diseaseOnsetDate: z.string().nullish(),
        icd9CodeId: z.string().nullish(),
        notes: z.string().nullish(),
        patientId: z.string().min(1),
        scores: z.array(TreatmentRequestScore).optional(),
        sector: TreatmentRequestSmall.shape['sector'],
        serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
        type: TreatmentRequestSmall.shape['type']
    })
    .extend(NotificationPayload.shape)
export type HttpCreateTreatmentRequestPayload = z.infer<typeof HttpCreateTreatmentRequestPayload>

export const httpCreateTreatmentRequest = async (payload: HttpCreateTreatmentRequestPayload) => {
    const response = await axios.post(`treatment-requests`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: TreatmentRequestSmall
    })
}

/*Delete treatment request*/
export const httpDeleteTreatmentRequest = ({urlParams}: {urlParams: {treatmentRequestId: string}}) =>
    axios.delete(`treatment-requests/${urlParams.treatmentRequestId}`)

/*Update treatment request*/
export const HttpUpdateTreatmentRequestPayload = z.object({
    authorization: TreatmentRequestAuthorization.nullable(),
    authorizationDate: z.string().nullish(),
    authorizationToRequest: z.boolean(),
    diseaseId: z.string().min(1),
    diseaseOnsetDate: z.string().nullish(),
    icd9CodeId: z.string().nullish(),
    notes: z.string().optional(),
    patientId: z.string().min(1),
    scores: z.array(TreatmentRequestScore).optional(),
    sector: TreatmentRequestSmall.shape['sector'],
    serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
    type: TreatmentRequestSmall.shape['type']
})
export type HttpUpdateTreatmentRequestPayload = z.infer<typeof HttpUpdateTreatmentRequestPayload>

export type HttpUpdateTreatmentRequestOptions = {
    urlParams: {treatmentRequestId: string}
    params: {responseType: ResponseType}
    payload: HttpUpdateTreatmentRequestPayload
}
export const httpUpdateTreatmentRequest = ({urlParams, params, payload}: HttpUpdateTreatmentRequestOptions) =>
    axios.patch(`treatment-requests/${urlParams.treatmentRequestId}`, payload, {params})

/* Update treatment request with signed report */
export type HttpUpdateSingleTreatmentRequestStatusPayload = {
    status: TreatmentRequestSmall['status']
    rejectedReason?: TreatmentRequestSmall['rejectedReason']
} & NotificationPayload

export interface HttpUpdateSingleTreatmentRequestStatusOptions {
    urlParams: {treatmentRequestId: string}
    payload: HttpUpdateSingleTreatmentRequestStatusPayload
}

export const httpUpdateSingleTreatmentRequestStatus = async ({
    urlParams,
    payload
}: HttpUpdateSingleTreatmentRequestStatusOptions) => {
    const response = await axios.patch(`treatment-requests/${urlParams.treatmentRequestId}/change-status`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: TreatmentRequestSmall
    })
}

/*Get treatment request*/
interface HttpGetTreatmentRequestOptions {
    urlParams: {treatmentRequestId: string}
    params: {responseType: ResponseType}
}
export const httpGetTreatmentRequest = ({urlParams, params}: HttpGetTreatmentRequestOptions) =>
    axios.get(`treatment-requests/${urlParams.treatmentRequestId}`, {params})

/*Get treatment request groups*/
export interface HttpGetTreatmentRequestGroupsOptions {
    urlParams: {treatmentRequestId: string}
    params: {
        group: 'therapist' | 'referring_doctor'
        distance?: number
        search?: string
        startDate?: string
        endDate?: string
    }
}
export const httpGetTreatmentRequestGroups = async ({urlParams, params}: HttpGetTreatmentRequestGroupsOptions) => {
    const response = await axios.get(`treatment-requests/${urlParams.treatmentRequestId}/index-groups`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: TreatmentRequestTherapistDoctor.array()
    })
}

/*Get treatment requests by patient*/
export interface HttpGetTreatmentRequestsByPatientOptions {
    urlParams: {
        patientId: string
    }
    params: PaginationParams & {
        search?: string
        status?: TreatmentRequestSmall['status']
    }
}

export const HttpGetTreatmentRequestsByPatientResponse = z.object({
    data: z.array(TreatmentRequestSmall),
    meta: PaginationResponse
})
export type HttpGetTreatmentRequestsByPatientResponse = z.infer<typeof HttpGetTreatmentRequestsByPatientResponse>

export const httpGetTreatmentRequestsByPatient = async ({
    urlParams,
    params
}: HttpGetTreatmentRequestsByPatientOptions) => {
    const response = await axios.get(`patients/${urlParams.patientId}/treatment-requests`, {
        params
    })

    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetTreatmentRequestsByPatientResponse
    })
}

/*follow ups*/
export interface HttpCreateFollowUpOptions {
    payload: {
        treatmentRequestId: string
        date: string
        notes?: string
    }
}
export const httpCreateFollowUp = ({payload}: HttpCreateFollowUpOptions) =>
    axios.post(`treatment-request-followups/`, payload)

export interface HttpDeleteFollowUpOptions {
    urlParams: {
        followUpId: string
    }
}
export const httpDeleteFollowUp = ({urlParams}: HttpDeleteFollowUpOptions) =>
    axios.delete(`treatment-request-followups/${urlParams.followUpId}`)

export interface HttpUpdateFollowUpOptions {
    urlParams: {
        followUpId: string
    }
    payload: {
        date?: string
        notes?: string | null
    }
}
export const httpUpdateFollowUp = ({urlParams, payload}: HttpUpdateFollowUpOptions) =>
    axios.patch(`treatment-request-followups/${urlParams.followUpId}`, payload)
